import { useEffect, useSyncExternalStore } from "react";
import { useLocation, useRouteLoaderData } from "react-router";

// "Hide chrome" lets another app (the Bouncie mobile app's in-app browser, an iframe)
// show a page without the site wrapper: header, footer, analytics/consent, cart.
//
// Triggers, all equivalent:
//   - `#hidechrome` in the URL hash. Legacy contract from bouncieweb; shipped app
//     versions send it, so it must keep working. The server never sees a hash.
//   - `?hidechrome=1`. Preferred for new consumers: the server sees it and renders
//     without chrome, so there is no flash.
//   - A previous trigger in this tab (sessionStorage), so in-page anchors and links
//     between legal pages stay chromeless. bouncieweb lost the flag on any navigation.
//
// The flag only applies on HIDE_CHROME_PATHS. Following a link out of a legal page
// into the rest of the site brings the full chrome back.

export const HIDE_CHROME_PATHS = [
  "/privacy-policy",
  "/terms-of-service",
  "/terms-of-use",
  "/find-my-obd-port",
] as const;

export const HIDE_CHROME_ATTR = "data-hide-chrome";
export const HIDE_CHROME_STORAGE_KEY = "bouncie:hidechrome";
const CHANGE_EVENT = "bouncie:hidechrome-change";

export function isHideChromePath(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return (HIDE_CHROME_PATHS as readonly string[]).includes(path);
}

/** Server-side: the explicit query-param trigger on an allowed path. */
export function isHideChromeRequest(url: URL): boolean {
  return url.searchParams.get("hidechrome") === "1" && isHideChromePath(url.pathname);
}

// Runs synchronously in <head>, before first paint and before the GTM bootstrap, so
// hash/sessionStorage triggers never flash chrome and GTM can see the attribute.
// Keep it ES5 and tiny; it is inlined into every page.
export const HIDE_CHROME_INLINE_SCRIPT = `(function(){try{
var p=${JSON.stringify(HIDE_CHROME_PATHS)},l=location,k=${JSON.stringify(HIDE_CHROME_STORAGE_KEY)},
path=l.pathname.length>1?l.pathname.replace(/\\/+$/,""):l.pathname;
if(p.indexOf(path)<0)return;
var on=l.hash==="#hidechrome"||/[?&]hidechrome=1(&|$)/.test(l.search);
try{if(on)sessionStorage.setItem(k,"1");else on=sessionStorage.getItem(k)==="1"}catch(e){}
if(on)document.documentElement.setAttribute(${JSON.stringify(HIDE_CHROME_ATTR)},"")
}catch(e){}})();`;

function subscribe(onChange: () => void) {
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => window.removeEventListener(CHANGE_EVENT, onChange);
}

function readAttr(): boolean {
  return document.documentElement.hasAttribute(HIDE_CHROME_ATTR);
}

function readSticky(): boolean {
  try {
    return sessionStorage.getItem(HIDE_CHROME_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Whether the current page should render without site chrome. The server answer (the
 * query param, via the root loader) is the hydration snapshot; after hydration the
 * `<html>` attribute set by HIDE_CHROME_INLINE_SCRIPT is the source of truth.
 */
export function useHideChrome(): boolean {
  const { pathname } = useLocation();
  const root = useRouteLoaderData("root") as { hideChrome?: boolean } | undefined;
  const flagged = useSyncExternalStore(subscribe, readAttr, () => root?.hideChrome === true);
  return flagged && isHideChromePath(pathname);
}

/**
 * Like useHideChrome, for components that render no markup and act in effects (cart
 * script, analytics, redirects). useHideChrome must answer with the server's value while
 * hydrating, which would let those effects fire once before the real answer arrives, so
 * this reads the `<html>` attribute directly. Only safe for components that return null:
 * their output cannot mismatch the server HTML.
 */
export function useHideChromeForEffects(): boolean {
  const { pathname } = useLocation();
  const root = useRouteLoaderData("root") as { hideChrome?: boolean } | undefined;
  const flagged = typeof document === "undefined" ? root?.hideChrome === true : readAttr();
  return flagged && isHideChromePath(pathname);
}

/**
 * Keeps the `<html>` attribute in step with client-side navigation: set when landing
 * on an allowed path with the sticky flag, cleared when leaving one. Mount once in root.
 */
export function useHideChromeSync(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const want = isHideChromePath(pathname) && (readSticky() || readAttr());
    const has = readAttr();
    if (want === has) return;
    if (want) document.documentElement.setAttribute(HIDE_CHROME_ATTR, "");
    else document.documentElement.removeAttribute(HIDE_CHROME_ATTR);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, [pathname]);
}
